import React, { useState, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useChatMessages } from "@/hooks/useChatMessages";
import { ChatPanel } from "@/components/settings/prompts/ChatPanel";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Bot, Grid2X2, Pencil, AlertCircle, Loader2, Check } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { testMistralAPI, testOpenAIAPI } from "@/services/apiTester";
import { createAgent, loadAgents } from "@/services/agentService";

const formSchema = z.object({
  name: z.string().min(2, {
    message: "O nome deve ter pelo menos 2 caracteres.",
  }),
  provider: z.string({
    required_error: "Selecione um provedor",
  }),
  apiKey: z.string().min(1, {
    message: "A chave API é obrigatória.",
  }),
  systemPrompt: z.string().min(1, {
    message: "O prompt do sistema é obrigatório.",
  }).max(65535, {
    message: "O prompt não pode exceder 65.535 caracteres."
  }),
  description: z.string().optional(),
});

type AgentFormData = z.infer<typeof formSchema>;

export default function AIAgents() {
  const { toast } = useToast();
  const [agents, setAgents] = useState<any[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { messages, chatInput, setChatInput, handleSendMessage } = useChatMessages();
  const [isLoading, setIsLoading] = useState(true);
  const [apiTestResult, setApiTestResult] = useState<string | null>(null);
  const [isTestingApi, setIsTestingApi] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoadingAgents, setIsLoadingAgents] = useState(false);

  const form = useForm<AgentFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      provider: "",
      apiKey: "",
      systemPrompt: "",
      description: "",
    },
  });

  const fetchAgents = async () => {
    try {
      setIsLoading(true);
      setIsLoadingAgents(true);
      const agentsList = await loadAgents();
      setAgents(agentsList);
    } catch (error: any) {
      console.error('Erro ao carregar agentes:', error);
      toast({
        title: "Erro ao carregar agentes",
        description: error.message || "Não foi possível carregar os agentes.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
      setIsLoadingAgents(false);
    }
  };

  const handleTestApiKey = async () => {
    const provider = form.getValues('provider');
    const apiKey = form.getValues('apiKey');

    if (!provider || !apiKey) {
      setApiTestResult("Selecione um provedor e insira uma chave API primeiro");
      return;
    }

    setIsTestingApi(true);
    setApiTestResult(null);

    try {
      let result;
      if (provider === 'mistral') {
        result = await testMistralAPI(apiKey);
      } else if (provider === 'openai') {
        result = await testOpenAIAPI(apiKey);
      } else {
        result = "Provedor não suportado para teste";
      }
      
      setApiTestResult(result);
    } catch (error: any) {
      setApiTestResult(`Erro ao testar: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
    } finally {
      setIsTestingApi(false);
    }
  };

  const getModelForProvider = (provider: string) => {
    switch (provider) {
      case 'mistral':
        return 'mistral-tiny';
      case 'openai':
        return 'gpt-4o-mini';
      default:
        return 'mistral-tiny';
    }
  };

  const handleSubmit = async (data: AgentFormData) => {
    try {
      setIsSubmitting(true);
      setError(null);
      setIsSuccess(false);
      
      console.log("Preparando dados para envio:", data);
      
      const model = getModelForProvider(data.provider);
      
      console.log("Iniciando criação do agente com modelo:", model);
      
      const newAgent = await createAgent({
        name: data.name,
        preferred_api: data.provider,
        api_key: data.apiKey,
        prompt: data.systemPrompt,
        model: model,
        description: data.description
      });
      
      console.log("Agente criado com sucesso:", newAgent);
      
      setAgents(prev => [newAgent, ...prev]);
      
      setIsSuccess(true);
      toast({
        title: "Sucesso",
        description: "Agente criado com sucesso!",
      });

      setTimeout(() => {
        form.reset();
        setIsSuccess(false);
      }, 1500);
      
    } catch (error: any) {
      console.error('Erro ao criar agente:', error);
      setError(error.message || "Não foi possível criar o agente.");
      
      toast({
        title: "Erro",
        description: error.message || "Não foi possível criar o agente. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAgentSelect = (agent: any) => {
    setSelectedAgent(agent);
  };

  const getPromptCharCount = () => {
    const value = form.watch("systemPrompt") || "";
    return value.length;
  };

  const isPromptTooLong = () => {
    const count = getPromptCharCount();
    return count > 60000;
  };

  const attachmentTypeAdapter = (_selectedAgent: Agent | null) => {
    return Promise.resolve();
  };

  return (
    <div className="space-y-6 p-4 md:p-8">
      <div>
        <h2 className="text-3xl font-bold">Agentes</h2>
        <p className="text-muted-foreground">Gerencie os agentes de IA do sistema</p>
      </div>

      <div className="grid gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div className="flex items-center space-x-2">
              <Grid2X2 className="h-6 w-6" />
              <CardTitle>Formulário de Agente</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            {isSuccess && (
              <Alert className="mb-4 bg-green-50 border-green-200">
                <Check className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-600">Agente criado com sucesso!</AlertDescription>
              </Alert>
            )}
            
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nome do Agente</FormLabel>
                        <FormControl>
                          <Input placeholder="Nome do agente" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="provider"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Provedor</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione um provedor" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="openai">OpenAI</SelectItem>
                            <SelectItem value="mistral">Mistral AI</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem className="md:col-span-2">
                        <FormLabel>Descrição (opcional)</FormLabel>
                        <FormControl>
                          <Input placeholder="Breve descrição do agente" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="flex flex-col space-y-2 md:col-span-2">
                    <FormField
                      control={form.control}
                      name="apiKey"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Chave API</FormLabel>
                          <div className="flex space-x-2">
                            <FormControl>
                              <Input
                                type="password"
                                placeholder="Chave API do provedor"
                                {...field}
                                className="flex-1"
                              />
                            </FormControl>
                            <Button 
                              type="button" 
                              variant="outline" 
                              onClick={handleTestApiKey}
                              disabled={isTestingApi}
                            >
                              {isTestingApi ? <Loader2 className="h-4 w-4 animate-spin" /> : "Testar"}
                            </Button>
                          </div>
                          <FormMessage />
                          {apiTestResult && (
                            <p className={`text-sm mt-1 ${apiTestResult.includes('válida') ? 'text-green-600' : 'text-red-600'}`}>
                              {apiTestResult}
                            </p>
                          )}
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <FormField
                  control={form.control}
                  name="systemPrompt"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Prompt do Sistema</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Instruções para o agente..."
                          className="min-h-[200px] h-64 resize-vertical"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                      <p className={`text-xs ${isPromptTooLong() ? 'text-red-500' : 'text-muted-foreground'} mt-1`}>
                        O prompt pode conter até 65.535 caracteres. Texto atual: {getPromptCharCount()} caracteres.
                      </p>
                    </FormItem>
                  )}
                />

                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full md:w-auto"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Criando...
                    </>
                  ) : isSuccess ? (
                    <>
                      <Check className="mr-2 h-4 w-4" />
                      Criado!
                    </>
                  ) : (
                    "Criar Agente"
                  )}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Agentes Cadastrados</CardTitle>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchAgents}
              disabled={isLoadingAgents}
            >
              {isLoadingAgents ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <span>Atualizar</span>
              )}
            </Button>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8">
                <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                <p className="text-muted-foreground">Carregando agentes...</p>
              </div>
            ) : agents.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Nenhum agente cadastrado ainda.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {agents.map((agent) => (
                  <Card key={agent.id} className="hover:shadow-lg transition-shadow">
                    <CardHeader className="flex flex-row items-center space-x-4 pb-2">
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={agent.avatar} alt={agent.name} />
                        <AvatarFallback>
                          <Bot className="h-6 w-6" />
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <CardTitle className="text-lg">{agent.name}</CardTitle>
                        <p className="text-sm text-muted-foreground">{agent.preferredApi}</p>
                      </div>
                      <Button variant="ghost" size="icon" className="ml-auto">
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="text-sm">
                        <strong>Modelo:</strong> {agent.model}
                      </div>
                      <div className="text-sm line-clamp-2">
                        <strong>Descrição:</strong> {agent.description || "Sem descrição"}
                      </div>
                      <div className="text-sm line-clamp-2">
                        <strong>Prompt:</strong> {agent.prompt && agent.prompt.substring(0, 50)}...
                      </div>
                      <Button 
                        variant="outline" 
                        className="w-full mt-4"
                        onClick={() => handleAgentSelect(agent)}
                      >
                        Selecionar e Testar
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {selectedAgent && (
          <Card>
            <CardHeader>
              <CardTitle>Testar Agente: {selectedAgent.name}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[500px] border rounded-lg">
                <ChatPanel
                  messages={messages}
                  chatInput={chatInput}
                  setChatInput={setChatInput}
                  handleSendMessage={handleSendMessage}
                  selectedAgent={selectedAgent}
                />
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
